import React from 'react';
import { Loader, AlertCircle, Clock } from 'lucide-react';
import { ProcessingStatusEnum } from '../../types/sop';

interface ProcessingStatusProps {
  isProcessing: boolean;
  status: ProcessingStatusEnum;
  message: string;
  error: string | null;
  formattedElapsedTime?: string;
  formattedProcessingTime?: string | null;
}

const ProcessingStatus: React.FC<ProcessingStatusProps> = ({
  isProcessing,
  status,
  message,
  formattedElapsedTime,
  formattedProcessingTime,
}) => {

  if (status === ProcessingStatusEnum.DONE) return null;
  if (!isProcessing && !formattedProcessingTime && status !== ProcessingStatusEnum.REGENERATE) {
    return null;
  }

  const getStatusIcon = () => {
    switch (status) {
      case ProcessingStatusEnum.QUEUED:
        return <Clock className="w-5 h-5 text-yellow-400" />;
      case ProcessingStatusEnum.PROCESSING:
      case ProcessingStatusEnum.PENDING_VERIFICATION:
      case ProcessingStatusEnum.REGENERATE:
        return <Loader className="w-8 h-8 text-blue-400 animate-spin" />;
      case ProcessingStatusEnum.ERROR:
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Loader className="w-8 h-8 text-blue-400 animate-spin" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case ProcessingStatusEnum.QUEUED:
        return 'from-yellow-500/20 to-orange-500/20 border-yellow-500/30';
      case ProcessingStatusEnum.PROCESSING:
      case ProcessingStatusEnum.PENDING_VERIFICATION:
      case ProcessingStatusEnum.REGENERATE:
        return 'from-blue-500/20 to-cyan-500/20 border-blue-500/30';
      case ProcessingStatusEnum.ERROR:
        return 'from-red-500/20 to-rose-500/20 border-red-500/30';
      default:
        return 'from-blue-500/20 to-cyan-500/20 border-blue-500/30';
    }
  };


  return (
    <div className="flex justify-center items-center min-h-[400px] mb-6">
      <div className="w-full max-w-2xl">
        <div className={`flex flex-col items-center gap-6 bg-gradient-to-br ${getStatusColor()} backdrop-blur-xl border p-8 rounded-xl`}>
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 flex items-center justify-center">
            {getStatusIcon()}
          </div>
          <div className="flex flex-col flex-1 text-center">
            <span className="font-bold text-white text-xl mb-2">{message}</span>
            <span className="text-slate-300 text-base">
              {status === ProcessingStatusEnum.QUEUED && 'Your request has been queued and will be processed shortly.'}
              {(status === ProcessingStatusEnum.PROCESSING || status === ProcessingStatusEnum.PENDING_VERIFICATION) && 'AI is analyzing your files. This may take a few minutes.'}
              {/* {status === ProcessingStatusEnum.REGENERATE && 'Updates were requested. Awaiting admin review.'} */}
              {status === ProcessingStatusEnum.ERROR && 'There was an issue processing your request.'}
              {!status && 'Preparing your files for analysis...'}
            </span>
            {status === ProcessingStatusEnum.ERROR && formattedProcessingTime && (
              <span className="text-red-400 font-semibold text-lg mt-2">
                (Failed after {formattedProcessingTime})
              </span>
            )}
            {status !== ProcessingStatusEnum.ERROR && formattedElapsedTime && (
              <span className="text-blue-400 font-semibold text-lg mt-2">
                ({formattedElapsedTime} elapsed)
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessingStatus;
